/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement } from 'react';
import Graph from './Graph';
import {
  DeviceType,
  ParameterAttribute,
  ParametersType,
  TimeSpan
} from '../../constants/telosair-interfaces';
import Loader from '../loader';
import { Filters } from '../../services/localstorage.service';
import Deque from '../../classes/deque';
import { arrayToObject } from '../../_helpers/globalHelper';
import { useUnifiedData } from '../../hooks/useUnifiedData';

type HistogramWidgetProps = {
  timeSpan: TimeSpan;
  extendedLabel: string;
  onChangeParameter: (value: ParametersType[]) => void;
  lineChartGraphData: Record<ParametersType, Deque>;
  loading: boolean;
  expanded: boolean;
  type: DeviceType;
  annotations?: any;
  maxTicksLimit?: number;
  showZoomControls?: boolean;
  /** Called when zoom/pan completes with the visible time range (start/end in ms). Use to refetch data. Options.immediate skips debounce. */
  onZoomTimeRangeChange?: (
    start: number,
    end: number,
    options?: { immediate?: boolean }
  ) => void;
  /** Full report time range (start/end ms). When provided with zoomed data, used to compute zoom % correctly after refetch. */
  fullReportRange?: { start: number; end: number } | null;
};

/**
 * Displays a histogram of parameter values and allows users to switch between parameters.
 * @param props - HistogramWidgetProps.
 * @returns A React element representing the Histogram component.
 */
export default function HistogramWidget(props: HistogramWidgetProps): ReactElement {
  const { parameters } = useUnifiedData();
  const {
    timeSpan,
    extendedLabel,
    lineChartGraphData,
    onChangeParameter,
    loading,
    expanded,
    type,
    annotations,
    maxTicksLimit,
    showZoomControls,
    onZoomTimeRangeChange,
    fullReportRange
  } = props;

  const labels = arrayToObject<ParameterAttribute>(parameters, 'parameterName', 'shortName');

  const hasData = lineChartGraphData && Object.keys(lineChartGraphData).length > 0;

  return (
    <div className="flex flex-1 justify-between flex-col relative">
      {loading ? (
        <Loader />
      ) : (
        <div className="flex flex-1 flex-col min-h-0 relative">
          {hasData ? (
            <Graph<ParametersType>
              labels={labels}
              useDynamicLineColorPlugin
              useTextPointPlugin
              storageKey={Filters.histogramParameter}
              lineChartGraphData={lineChartGraphData}
              timeSpan={timeSpan}
              onChangeLabel={onChangeParameter}
              type={type}
              annotations={annotations}
              maxTicksLimit={maxTicksLimit}
              showZoomControls={showZoomControls}
              onZoomTimeRangeChange={onZoomTimeRangeChange}
              fullReportRange={fullReportRange}
            />
          ) : (
            <div className="flex flex-1 justify-center items-center h-full text-gray-400 font-medium">
              No Data Available
            </div>
          )}
        </div>
      )}
    </div>
  );
}
